import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ChatService, PayPalOrderResponse } from './chat.service';
import { QuickActionResponse, QuickActionType } from '../models/quick-action.model';

interface QuickActionApiResponse {
  success: boolean;
  data: QuickActionResponse;
}

@Injectable({ providedIn: 'root' })
export class QuickActionService {
  private readonly http = inject(HttpClient);
  private readonly chatService = inject(ChatService);
  private readonly baseUrl = '/api/quick-actions';
  private actionId: string | null = null;

  start(actionType: QuickActionType, payload?: Record<string, unknown>): Observable<QuickActionResponse> {
    this.actionId = null;
    return this.post('start', {
      action_type: actionType,
      ...(payload ? { payload } : {}),
    });
  }

  step(actionType: QuickActionType, step: string, payload: Record<string, unknown>): Observable<QuickActionResponse> {
    return this.post('step', { action_type: actionType, step, payload });
  }

  complete(actionType: QuickActionType, step: string, payload: Record<string, unknown>): Observable<QuickActionResponse> {
    return this.post('complete', { action_type: actionType, step, payload });
  }

  downloadInstallmentContract(payload: Record<string, unknown>): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.baseUrl}/installment-contract-pdf`, {
      action_type: 'payment',
      ...(this.actionId ? { action_id: this.actionId } : {}),
      session_id: this.chatService.getSessionId(),
      payload,
    }, { observe: 'response', responseType: 'blob' }).pipe(catchError(async (error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || !(error.error instanceof Blob) || error.error.type !== 'application/json') {
        throw error;
      }
      let body: unknown;
      try { body = JSON.parse(await error.error.text()); } catch { throw error; }
      throw new HttpErrorResponse({ error: body, headers: error.headers, status: error.status,
        statusText: error.statusText, url: error.url ?? undefined });
    }));
  }

  startPaypalFullPayment(): Observable<PayPalOrderResponse> {
    return this.chatService.createPaypalOrder('quick_action_full_payment');
  }

  private post(path: 'start' | 'step' | 'complete', body: Record<string, unknown>): Observable<QuickActionResponse> {
    const sessionId = this.chatService.getSessionId();
    return this.http.post<QuickActionApiResponse>(`${this.baseUrl}/${path}`, {
      ...body,
      ...(path !== 'start' && this.actionId ? { action_id: this.actionId } : {}),
      session_id: sessionId,
    }).pipe(map((res) => {
      this.actionId = res.data.action_id;
      return res.data;
    }));
  }
}
